import * as React from "react";
import cx from "classnames";
import isNil from "lodash/isNil";
import { I18n } from "react-redux-i18n";

import { getTestAttributes } from "@common/utils/tests";
import { Button } from "@common/components/Button";
import { MODULE_NAME } from "@modules/fhq-design-tool/constants/shared";
import { IInputComponent } from "@modules/fhq-design-tool/types/input-component";
import { Ellipsis } from "@common/components/Ellipsis";
import { Flex } from "@common/components/Flex";
import FormatManagement, { FORMAT_MANAGEMENT_MODE } from "@common/components/FormatManagement";
import { FolderFormatsDto, FormatFoldersDto } from "@common/dto/folder-settings";
import { FolderFormatConfig } from "@common/actions/formats";

import { Field, FieldError } from "../field";

import "./format-picker.styles.scss";

export type FormatPickerOnChangeValue = {
  format: FolderFormatsDto;
};

export type FormatPickerProps = IInputComponent<string | undefined, FormatPickerOnChangeValue | undefined> & {
  clearable?: boolean;
  errors?: Array<FieldError>;
  labelWidth?: number | string;
  label: string;
  getFolderFormats: (queries?: FolderFormatConfig["queries"]) => Promise<FolderFormatsDto[]>;
  getFormatFolders: (queries?: FolderFormatConfig["queries"]) => Promise<FormatFoldersDto[]>;
  placeholder?: string;
};

export const FormatPicker: React.FC<FormatPickerProps> = (props) => {
  const {
    placeholder,
    errors,
    className,
    disabled,
    label,
    labelWidth,
    onChange,
    value,
    metadata,
    description,
    required,
    margin,
    clearable,
    getFolderFormats,
    getFormatFolders,
  } = props;

  const [selectorOpened, setSelectorOpenedState] = React.useState(false);
  const [folders, setFolders] = React.useState<FormatFoldersDto[]>([]);
  const [formats, setFormats] = React.useState<FolderFormatsDto[]>([]);

  const closeSelector = React.useCallback(() => {
    setSelectorOpenedState(false);
  }, []);

  const handleCloseSelector = React.useCallback(() => {
    closeSelector();
    setFormats([]);
    setFolders([]);
  }, [closeSelector]);

  const openSelector = React.useCallback(() => {
    setSelectorOpenedState(true);
  }, []);

  const handleFormatChange = React.useCallback(
    (format: FolderFormatsDto) => {
      onChange({
        format,
      });
      handleCloseSelector();
    },
    [onChange, handleCloseSelector]
  );

  const handleClear = React.useCallback(() => {
    onChange(undefined);
  }, [onChange]);

  const handleGetFolders = React.useCallback(() => {
    return getFormatFolders().then((folders) => {
      setFolders(folders);
    });
  }, []);

  const handleGetFormats = React.useCallback((folderId: number) => {
    return getFolderFormats({ folderId }).then((formats) => {
      setFormats(formats);
    });
  }, []);

  return (
    <Field
      errors={errors}
      labelWidth={labelWidth}
      label={label}
      disabled={disabled}
      className={cx("FhqDesignToolFormatPicker", className)}
      description={description}
      required={required}
      margin={margin}
      classes={{
        input: "FhqDesignToolFormatPicker__input",
      }}
    >
      <Flex className="FhqDesignToolFormatPicker__value" alignItems="center">
        <Ellipsis
          maxWidth="calc(100% - 50px)"
          text={value ?? placeholder ?? I18n.t("Select Field")}
          className={cx({
            ["FhqDesignToolFormatPicker__placeholder"]: isNil(value),
          })}
        />

        <Flex justify="center" alignItems="center" className="FhqDesignToolFormatPicker__actions">
          {clearable && !isNil(value) && (
              <Button
                  {...getTestAttributes(MODULE_NAME, metadata.description, "clear-btn")}
                  className={cx("FhqDesignToolFormatPicker__clear-btn", "FhqDesignToolFormatPicker__action-btn")}
                  onClick={() => {handleClear()}}
                  variant="text"
              >
                <i className="fa fa-close"></i>
              </Button>
          )}

          <Button
            {...getTestAttributes(MODULE_NAME, metadata.description, "trigger-btn")}
            className={cx("FhqDesignToolFormatPicker__actions__trigger-btn", "FhqDesignToolFormatPicker__action-btn")}
            onClick={openSelector}
            variant="text"
          >
            <i className="fa fa-edit"></i>
          </Button>
        </Flex>
      </Flex>

      {selectorOpened && (
        <FormatManagement
          mode={FORMAT_MANAGEMENT_MODE.load}
          folders={folders}
          formats={formats}
          getFolders={handleGetFolders}
          getFormats={handleGetFormats}
          close={handleCloseSelector}
          onSubmit={handleFormatChange}
        />
      )}
    </Field>
  );
};
